import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  Loader2, RefreshCw, Camera, ExternalLink, ChevronLeft, ChevronRight, X,
  ArrowDownToLine, ArrowUpFromLine, Package, MapPin, Route as RouteIcon, Clock,
} from 'lucide-react';
import api from '../../api/auth.api';

/**
 * Lowboy Location (rebuilt) — the trucker's day on a map.
 *
 * Doug is preselected (only real driver). The map shows the truck live, the
 * day's GPS trail, and numbered haul-log stops; tiles show miles/stops/drive
 * time; the camera card defaults to the AUX (trailer) camera — Cliff wants to
 * SEE the load, not the road. Timeline mirrors Trucker Day (GPS stops + what
 * was picked up / dropped, driver entries, dump loads).
 *
 * Data: /api/trucker-location/drivers (who) → /api/cdl/v2/day (shifts, stops,
 * loads) → /api/fleet-vehicles/:truck/trips?date (trail + miles) →
 * /api/fleet-vehicles (live position) → /api/trucker-location/vehicle/:vid/all-cameras.
 */

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || 'pk.eyJ1IjoiY2xpZmZpc29uIiwiYSI6ImNtNHZqZ3p1cTA3MmcycHE2bDdtdmlwdmYifQ.6kIxcCZXH6I9gHe41FhkMw';
mapboxgl.accessToken = MAPBOX_TOKEN;

const FALLBACK_TRUCK_ID = 409; // Pete 589 — Doug's truck when no shift row exists

interface Driver {
  employee_id: number; first_name: string; last_name: string;
  samsara_vehicle_id: string | null; clocked_in_at: string | null;
  current_job_name: string | null; equipment_count: number;
}
interface Shift { shift_id: number; truck_equipment_id: number; started_at: string; ended_at: string | null; truck_name?: string; truck_number?: string; }
interface StopMovement { movement_id: number; name: string; status: string; }
interface HaulStop {
  stop_id: number; name: string; arrived_at: string; departed_at: string | null;
  dwell_minutes: number | null; lat: number | null; lng: number | null;
  loaded: StopMovement[]; dropped: StopMovement[];
}
interface DriverEvent {
  movement_id: number; action: 'load' | 'unload'; timestamp: string; is_rental: boolean;
  rental_company?: string; rental_equipment_type?: string; rental_description?: string;
  equipment_name?: string; bradanna_equipment_number?: string; job_name?: string;
}
interface DumpLoad { load_id: number; material_type: string; load_count: number; timestamp: string; job_name?: string; }
interface GpsEvent { movement_id: number; name: string; status: string; detected_at: string; action: 'load' | 'unload'; job_name: string | null; }
interface DayData {
  shifts: Shift[]; stops: HaulStop[]; gps_events?: GpsEvent[]; driver_events: DriverEvent[]; dump_loads: DumpLoad[];
  live: { moving: boolean; on_trailer: { equipment_id: number; name: string }[] } | null;
}
interface Trip { start_time: string; end_time: string; from?: string | null; to?: string | null; distance_miles: number; duration_min: number; path: [number, number][]; }
interface TripData { points: { lng: number; lat: number; time?: string }[]; trips: Trip[]; total_distance_miles: number; total_drive_min: number; }
interface LiveVehicle {
  equipment_id: number; lat: number | null; lng: number | null; speed_mph: number | null;
  status: string; address: string | null; synced_at: string | null;
  current_job: { job_id: number; job_name: string } | null;
  current_place: { place_id: number; name: string } | null;
}
interface CameraStill { url: string; input: string; startTime?: string; capturedAtTime?: string; }

const CAM_LABELS: Record<string, string> = {
  dashcamRoadFacing: 'Road', dashcamDriverFacing: 'Cab',
  analog1: 'Trailer 1', analog2: 'Trailer 2', analog3: 'Trailer 3',
};
// Same hauler marker as the Trucks/Fleet pages: orange circle + truck outline,
// red badge showing how many machines are aboard.
const TRUCK_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0f172a" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>`;
const truckMarkerHTML = (aboard: number) => {
  const badge = aboard > 0 ? `<span style="position:absolute;top:-4px;right:-4px;background:#dc2626;color:#fff;font-size:10px;font-weight:700;border-radius:9px;padding:1px 5px;border:1.5px solid #fff;line-height:1;">${aboard}</span>` : '';
  return `<span style="position:relative;width:32px;height:32px;border-radius:50%;background:#f59e0b;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;">${TRUCK_SVG}${badge}</span>`;
};

// Fleet-page naming for machine pills: drop the leading unit number the API
// prepends, then move the model token to the front ("Cat 270" → "270 Cat").
const pillName = (raw?: string): string => {
  const parts = (raw || '').trim().split(/\s+/);
  if (parts.length > 1 && /^\d{3,}$/.test(parts[0])) parts.shift();
  const last = parts[parts.length - 1];
  if (parts.length >= 2 && /\d/.test(last)) return [last, ...parts.slice(0, -1)].join(' ');
  return parts.join(' ');
};

const estToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
const addDays = (ymd: string, n: number) => {
  const d = new Date(`${ymd}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const fmtTime = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) : '—';
const fmtAgo = (iso: string | null | undefined) => {
  if (!iso) return '';
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  return `${Math.floor(min / 60)}h ${min % 60}m ago`;
};
const fmtDrive = (min: number | null | undefined) => {
  const m = Math.round(min || 0);
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`;
};

const card: React.CSSProperties = {
  background: '#fff', borderRadius: 12, padding: 14, marginBottom: 12,
  boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb',
};
const chip = (bg: string, fg: string): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, background: bg, color: fg,
  borderRadius: 999, padding: '3px 10px', fontSize: 13, fontWeight: 600, margin: '2px 4px 2px 0',
});

export const TruckerTracking: React.FC = () => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [driver, setDriver] = useState<Driver | null>(null);
  const [clockedInAt, setClockedInAt] = useState<string | null>(null);
  const [date, setDate] = useState(estToday());
  const [day, setDay] = useState<DayData | null>(null);
  const [trips, setTrips] = useState<TripData | null>(null);
  const [vehicle, setVehicle] = useState<LiveVehicle | null>(null);
  const [truckName, setTruckName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [cams, setCams] = useState<CameraStill[]>([]);
  const [camsLoading, setCamsLoading] = useState(false);
  // Hard default: camera 4 (analog3, the trailer view) every open — session-only switching.
  const [featuredCam, setFeaturedCam] = useState<string>('analog3');
  const [camModal, setCamModal] = useState(false);
  const [liveShareLoading, setLiveShareLoading] = useState(false);

  const isToday = date === estToday();
  // loadCams is declared below; effects above it reach it through this ref.
  const loadCamsRef = useRef<(() => Promise<void>) | null>(null);

  // ── Drivers (Doug preselected — he's the only active truck_driver) ─────────
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/api/trucker-location/drivers');
        const list: Driver[] = r.data?.data || [];
        setDrivers(list);
        setDriver(list[0] ?? null);
      } catch { setError('Could not load drivers'); }
    })();
  }, []);

  // ── Day + trail + live position ─────────────────────────────────────────────
  const loadAll = useCallback(async (quiet = false) => {
    if (!driver) return;
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const dayRes = await api.get(`/api/cdl/v2/day?employee_id=${driver.employee_id}&date=${date}`);
      const d: DayData = dayRes.data?.data;
      setDay(d);
      const lastShift = d?.shifts?.[d.shifts.length - 1];
      const tid = lastShift?.truck_equipment_id ?? FALLBACK_TRUCK_ID;
      setTruckName(lastShift?.truck_name ?? 'Pete 589');

      const [tripsRes, vehRes, drvRes] = await Promise.all([
        api.get(`/api/fleet-vehicles/${tid}/trips?date=${date}`),
        isToday ? api.get('/api/fleet-vehicles?positions=1') : Promise.resolve(null),
        isToday ? api.get('/api/trucker-location/drivers') : Promise.resolve(null),
      ]);
      setTrips(tripsRes.data?.data ?? null);
      if (vehRes) {
        const v = (vehRes.data?.data?.vehicles || []).find((x: LiveVehicle) => x.equipment_id === tid);
        setVehicle(v ?? null);
      } else setVehicle(null);
      if (drvRes) {
        const me = (drvRes.data?.data || []).find((d: Driver) => d.employee_id === driver.employee_id);
        setClockedInAt(me?.clocked_in_at ?? null);
      } else setClockedInAt(null);
    } catch {
      if (!quiet) setError('Could not load the day — tap refresh to retry');
    } finally { if (!quiet) setLoading(false); }
  }, [driver, date, isToday]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!isToday) return;
    const t = setInterval(() => loadAll(true), 45_000);
    return () => clearInterval(t);
  }, [isToday, loadAll]);

  // Refresh when the app regains focus. Installed PWAs skip visibilitychange on
  // reopen, so listen for focus + pageshow too.
  useEffect(() => {
    const onWake = () => {
      if (document.visibilityState === 'hidden') return;
      loadAll(true);
      if (isToday) loadCamsRef.current?.();
    };
    const onVis = () => { if (document.visibilityState === 'visible') onWake(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onWake);
    window.addEventListener('pageshow', onWake);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onWake);
      window.removeEventListener('pageshow', onWake);
    };
  }, [loadAll, isToday]);

  // Pull-to-refresh: drag down ≥70px from the top of the page.
  const [pulling, setPulling] = useState(false);
  useEffect(() => {
    let startY = 0, armed = false;
    const onStart = (e: TouchEvent) => {
      armed = window.scrollY <= 0;
      startY = e.touches[0].clientY;
    };
    const onMove = (e: TouchEvent) => {
      if (!armed) return;
      if (e.touches[0].clientY - startY > 70) {
        armed = false;
        setPulling(true);
        Promise.all([loadAll(true), isToday ? loadCamsRef.current?.() : null])
          .finally(() => setPulling(false));
      }
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
    };
  }, [loadAll, isToday]);

  // ── Cameras (today only) ────────────────────────────────────────────────────
  const loadCams = useCallback(async () => {
    if (!driver?.samsara_vehicle_id) { setCams([]); return; }
    setCamsLoading(true);
    try {
      const r = await api.get(`/api/trucker-location/vehicle/${driver.samsara_vehicle_id}/all-cameras`);
      setCams(r.data?.data?.cameras || r.data?.cameras || []);
    } catch { /* camera card just shows retry */ }
    finally { setCamsLoading(false); }
  }, [driver]);

  useEffect(() => { loadCamsRef.current = loadCams; }, [loadCams]);
  useEffect(() => { if (isToday) loadCams(); }, [loadCams, isToday]);

  const openLiveShare = async () => {
    if (!driver?.samsara_vehicle_id) return;
    setLiveShareLoading(true);
    try {
      const r = await api.get(`/api/samsara/live-share/${driver.samsara_vehicle_id}`);
      const url = r.data?.data?.url || r.data?.url;
      if (url) window.open(url, '_blank');
    } catch { /* ignore */ }
    finally { setLiveShareLoading(false); }
  };

  // ── Map ─────────────────────────────────────────────────────────────────────
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const mapReady = useRef(false);
  const truckMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const stopMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const fittedKeyRef = useRef('');
  const savedViewRef = useRef<{ center: mapboxgl.LngLat; zoom: number } | null>(null);
  const zoomToggleRef = useRef<(() => void) | null>(null);

  const drawMap = useCallback(() => {
    const map = mapRef.current;
    if (!map || !mapReady.current) return;

    // Trail (muted) — clipped to the driver's shift window so the map shows HIS
    // day from clock-in, not everything the truck did before it.
    const winFrom = day?.shifts?.length ? new Date(day.shifts[0].started_at).getTime() : null;
    const lastSh = day?.shifts?.length ? day.shifts[day.shifts.length - 1] : null;
    const winTo = lastSh ? (lastSh.ended_at ? new Date(lastSh.ended_at).getTime() : Infinity) : null;
    const coords: [number, number][] = (trips?.points || [])
      .filter(p => p.lng != null && p.lat != null
        && (winFrom == null || !p.time
            || (new Date(p.time).getTime() >= winFrom && new Date(p.time).getTime() <= (winTo ?? Infinity))))
      .map(p => [Number(p.lng), Number(p.lat)]);
    const src = map.getSource('trail') as mapboxgl.GeoJSONSource | undefined;
    if (src) src.setData(coords.length >= 2
      ? { type: 'Feature' as const, geometry: { type: 'LineString' as const, coordinates: coords }, properties: {} }
      : { type: 'FeatureCollection' as const, features: [] });

    // Current run = the LAST trip of the day, bold on top (like the Trucks page).
    const lastTrip = trips?.trips?.length ? trips.trips[trips.trips.length - 1] : null;
    const runSrc = map.getSource('run') as mapboxgl.GeoJSONSource | undefined;
    if (runSrc) runSrc.setData(lastTrip?.path && lastTrip.path.length >= 2
      ? { type: 'Feature' as const, geometry: { type: 'LineString' as const, coordinates: lastTrip.path }, properties: {} }
      : { type: 'FeatureCollection' as const, features: [] });

    // Stop pins (numbered, from the haul log)
    stopMarkersRef.current.forEach(m => m.remove());
    stopMarkersRef.current = [];
    (day?.stops || []).forEach((s, i) => {
      if (s.lat == null || s.lng == null) return;
      const el = document.createElement('div');
      el.style.cssText = 'width:24px;height:24px;border-radius:50%;background:#1e40af;color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)';
      el.textContent = String(i + 1);
      const pop = new mapboxgl.Popup({ offset: 14, closeButton: false }).setHTML(
        `<strong>${s.name}</strong><br/>${fmtTime(s.arrived_at)} – ${s.departed_at ? fmtTime(s.departed_at) : 'here'}`
      );
      const m = new mapboxgl.Marker({ element: el }).setLngLat([Number(s.lng), Number(s.lat)]).setPopup(pop).addTo(map);
      stopMarkersRef.current.push(m);
    });

    // Truck marker — live position today, else end of trail
    const pos: [number, number] | null =
      vehicle?.lng != null && vehicle?.lat != null ? [Number(vehicle.lng), Number(vehicle.lat)]
        : coords.length ? coords[coords.length - 1] : null;
    const aboard = day?.live?.on_trailer?.length ?? 0;
    if (pos) {
      if (!truckMarkerRef.current) {
        const el = document.createElement('div');
        el.style.zIndex = '10';   // stop pins are re-added after this marker — keep the truck on top
        el.innerHTML = truckMarkerHTML(aboard);
        truckMarkerRef.current = new mapboxgl.Marker({ element: el }).setLngLat(pos).addTo(map);
      } else {
        truckMarkerRef.current.setLngLat(pos);
        truckMarkerRef.current.getElement().innerHTML = truckMarkerHTML(aboard);
      }
    } else if (truckMarkerRef.current) {
      truckMarkerRef.current.remove(); truckMarkerRef.current = null;
    }

    // Fit once per driver+date (not on every poll)
    const key = `${driver?.employee_id}|${date}`;
    if (fittedKeyRef.current !== key) {
      const b = new mapboxgl.LngLatBounds();
      coords.forEach(c => b.extend(c));
      (day?.stops || []).forEach(s => { if (s.lat != null && s.lng != null) b.extend([Number(s.lng), Number(s.lat)]); });
      if (pos) b.extend(pos);
      if (!b.isEmpty()) {
        map.fitBounds(b, { padding: 46, maxZoom: 14, duration: 400 });
        fittedKeyRef.current = key;
        savedViewRef.current = null;   // new day view — a stale zoom-toggle return point would be wrong
      }
    }
  }, [trips, day, vehicle, driver, date]);

  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;
    const map = new mapboxgl.Map({
      container: mapContainer.current, style: 'mapbox://styles/mapbox/streets-v12',
      center: [-81.7, 26.2], zoom: 9,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    // PERMANENTLY locked for pan/scroll so the page always scrolls past the big
    // map. Double-tap / double-click = zoom in on the truck (repeatable — each
    // one zooms tighter); the +/- buttons zoom back out.
    map.scrollZoom.disable(); map.dragPan.disable(); map.doubleClickZoom.disable();
    map.touchZoomRotate.disable(); map.touchPitch?.disable();
    // Toggle: first double-tap zooms in on the truck; the next one returns to
    // the view you were at (normally the whole-day fit).
    const zoomToTruck = () => {
      if (savedViewRef.current) {
        map.flyTo({ ...savedViewRef.current, duration: 600 });
        savedViewRef.current = null;
        return;
      }
      const p = truckMarkerRef.current?.getLngLat();
      if (!p) return;
      savedViewRef.current = { center: map.getCenter(), zoom: map.getZoom() };
      map.flyTo({ center: p, zoom: 15, duration: 600 });
    };
    zoomToggleRef.current = zoomToTruck;
    // Double-tap detection rides Mapbox's own 'click' events — it synthesizes a
    // click per tap on mobile, where a raw touchend counter on the canvas is
    // unreliable. Two clicks within 350ms (mouse dblclick produces them too) = toggle.
    let lastClick = 0;
    map.on('click', () => {
      const t = Date.now();
      if (t - lastClick < 350) { lastClick = 0; zoomToTruck(); }
      else lastClick = t;
    });
    map.on('load', () => {
      const empty = { type: 'FeatureCollection' as const, features: [] };
      // Whole-day trail (muted) …
      map.addSource('trail', { type: 'geojson', data: empty });
      map.addLayer({
        id: 'trail-line', type: 'line', source: 'trail',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#94a3b8', 'line-width': 3, 'line-opacity': 0.55 },
      });
      // … with the CURRENT RUN on top (bold blue, like the Trucks page).
      map.addSource('run', { type: 'geojson', data: empty });
      map.addLayer({
        id: 'run-line', type: 'line', source: 'run',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#2563eb', 'line-width': 4.5, 'line-opacity': 0.9 },
      });
      mapReady.current = true;
      drawMap();
    });
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; mapReady.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { drawMap(); }, [drawMap]);

  // ── Timeline (same visual language as Trucker Day) ─────────────────────────
  type Item =
    | { kind: 'stop'; time: string; stop: HaulStop; idx: number }
    | { kind: 'gps'; time: string; ge: GpsEvent }
    | { kind: 'driver'; time: string; ev: DriverEvent }
    | { kind: 'dump'; time: string; dl: DumpLoad };
  const timeline: Item[] = [
    ...(day?.stops || []).map((s, idx) => ({ kind: 'stop' as const, time: s.arrived_at, stop: s, idx })),
    ...(day?.gps_events || []).map(ge => ({ kind: 'gps' as const, time: ge.detected_at, ge })),
    ...(day?.driver_events || []).map(ev => ({ kind: 'driver' as const, time: ev.timestamp, ev })),
    ...(day?.dump_loads || []).map(dl => ({ kind: 'dump' as const, time: dl.timestamp, dl })),
  ].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

  const evLabel = (ev: DriverEvent) => {
    if (ev.is_rental && ev.rental_company === 'MISC') return ev.rental_description || 'Parts / materials';
    if (ev.is_rental) return `${ev.rental_company || 'Rental'} ${ev.rental_equipment_type || ''}`.trim();
    return `${ev.bradanna_equipment_number ? ev.bradanna_equipment_number + ' ' : ''}${ev.equipment_name || 'Equipment'}`;
  };

  const featured = cams.find(c => c.input === featuredCam) || cams[0] || null;
  const pickCam = (input: string) => setFeaturedCam(input);

  // Scope miles/driving to the DRIVER's shift window — the trips feed covers the
  // whole truck-day, but his board should start at his clock-in.
  const shiftStartMs = day?.shifts?.length ? new Date(day.shifts[0].started_at).getTime() : null;
  const shiftEndMs = day?.shifts?.length
    ? (day.shifts[day.shifts.length - 1].ended_at ? new Date(day.shifts[day.shifts.length - 1].ended_at as string).getTime() : Date.now())
    : null;
  const shiftTrips = (trips?.trips || []).filter(t =>
    shiftStartMs == null
    || (new Date(t.end_time).getTime() > shiftStartMs && new Date(t.start_time).getTime() < (shiftEndMs ?? Infinity)));
  const miles = trips
    ? (shiftStartMs != null
        ? shiftTrips.reduce((s, t) => s + Number(t.distance_miles || 0), 0).toFixed(1)
        : Number(trips.total_distance_miles ?? 0).toFixed(1))
    : '—';
  const driveMin = shiftStartMs != null
    ? shiftTrips.reduce((s, t) => s + Number(t.duration_min || 0), 0)
    : trips?.total_drive_min;
  const onTrailer = day?.live?.on_trailer ?? [];

  // Current run — "Left Mason 8:12 AM with skid loader + mini" style summary.
  const openStop = (day?.stops || []).find(s => !s.departed_at) ?? null;
  const departedStops = (day?.stops || []).filter(s => s.departed_at);
  const lastDeparted = departedStops.length ? departedStops[departedStops.length - 1] : null;
  const lastRun = trips?.trips?.length ? trips.trips[trips.trips.length - 1] : null;
  // "Left at" time: prefer the GPS-derived trip start (matches the Trucks page's
  // trip details) over the detector's departed_at, which is stamped a few minutes
  // late — but only when the two clearly describe the SAME departure.
  const leftAt = (lastRun?.start_time && lastDeparted?.departed_at
    && Math.abs(new Date(lastRun.start_time).getTime() - new Date(lastDeparted.departed_at).getTime()) < 15 * 60000)
    ? lastRun.start_time : lastDeparted?.departed_at ?? null;

  // GPS-first departure: if the truck is MOVING right now, he's left the last
  // stop — even while the detector's settle pass still has it open (that lag
  // made this card read "At Woodlawn" for ~6 min after pull-out).
  const movingNow = isToday && vehicle?.status === 'moving';
  const leftStop = movingNow ? (openStop ?? lastDeparted) : null;
  const leftStopTime = leftStop === openStop
    ? (lastRun?.start_time && openStop && new Date(lastRun.start_time) > new Date(openStop.arrived_at) ? lastRun.start_time : null)
    : leftAt;
  // Job → address-book place → street address (backend reverse-geocodes moving
  // trucks now) → "on the road"/"stopped" — never raw coordinates, and never
  // blank while we have a fix (the geocode cache is null on first sight and
  // places need 5 min of dwell, so the old chain regularly came up all-empty).
  const gpsStale = isToday && vehicle?.status === 'offline';
  const nowAt = vehicle
    ? (vehicle.current_job?.job_name || vehicle.current_place?.name || vehicle.address
       || (movingNow ? 'on the road' : vehicle.lat != null ? 'stopped' : null))
    : null;

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: '12px 12px 90px' }}>
      {/* Header: driver + date nav */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          {drivers.length > 1 ? (
            <select
              value={driver?.employee_id ?? ''}
              onChange={e => setDriver(drivers.find(d => d.employee_id === Number(e.target.value)) ?? null)}
              style={{ fontSize: 17, fontWeight: 800, border: 'none', background: 'none' }}
            >
              {drivers.map(d => <option key={d.employee_id} value={d.employee_id}>{d.first_name} {d.last_name}</option>)}
            </select>
          ) : (
            <div style={{ fontSize: 18, fontWeight: 800 }}>{driver ? `${driver.first_name} ${driver.last_name}` : '—'}</div>
          )}
          <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            🚛 {truckName}
            {isToday && (
              <span style={{ fontWeight: 600 }}>
                {clockedInAt ? `· Clocked in ${fmtTime(clockedInAt)}` : '· Not clocked in'}
              </span>
            )}
            {isToday && vehicle && (
              <span style={chip(vehicle.status === 'moving' ? '#dcfce7' : '#f3f4f6', vehicle.status === 'moving' ? '#166534' : '#374151')}>
                {vehicle.status === 'moving' ? `${Math.round(vehicle.speed_mph || 0)} mph` : vehicle.status}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          <button onClick={() => setDate(d => addDays(d, -1))} style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 8, padding: 6, cursor: 'pointer' }}><ChevronLeft size={16} /></button>
          <span style={{ fontSize: 13, fontWeight: 700, minWidth: 44, textAlign: 'center' }}>{isToday ? 'Today' : date.slice(5)}</span>
          <button onClick={() => setDate(d => addDays(d, 1))} disabled={isToday} style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 8, padding: 6, opacity: isToday ? 0.4 : 1, cursor: 'pointer' }}><ChevronRight size={16} /></button>
        </div>
      </div>

      {pulling && (
        <div style={{ textAlign: 'center', padding: '4px 0 10px' }}>
          <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      )}

      {error && <div style={{ ...card, background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', fontSize: 13 }}>{error}</div>}

      {/* Stat tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
        {[
          { icon: <RouteIcon size={15} />, label: 'Miles', value: miles },
          { icon: <MapPin size={15} />, label: 'Stops', value: String(day?.stops?.length ?? 0) },
          { icon: <Clock size={15} />, label: 'Driving', value: fmtDrive(driveMin) },
          { icon: <Clock size={15} />, label: 'Clocked in', value: clockedInAt ? fmtDrive((Date.now() - new Date(clockedInAt).getTime()) / 60000) : '—' },
        ].map(t => (
          <div key={t.label} style={{ ...card, marginBottom: 0, padding: '10px 6px', textAlign: 'center' }}>
            <div style={{ fontSize: 19, fontWeight: 800 }}>{t.value}</div>
            <div style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>{t.icon}{t.label}</div>
          </div>
        ))}
      </div>

      {/* Current run — where he left from, what's on the trailer */}
      <div style={card}>
        <h3 style={{ margin: '0 0 6px', fontSize: 15, display: 'flex', alignItems: 'center', gap: 6 }}>
          <RouteIcon size={16} /> Current run
        </h3>
        <div style={{ fontSize: 15, fontWeight: 700 }}>
          {leftStop
            ? <>Left {leftStop.name} <span style={{ color: '#6b7280', fontWeight: 400 }}>
                {leftStopTime ? `· ${fmtAgo(leftStopTime)} (${fmtTime(leftStopTime)})` : '· just now'}
              </span></>
            : openStop
              ? <>At {openStop.name} <span style={{ color: '#6b7280', fontWeight: 400 }}>since {fmtTime(openStop.arrived_at)}</span></>
              : lastDeparted
                ? <>Left {lastDeparted.name} <span style={{ color: '#6b7280', fontWeight: 400 }}>· {fmtAgo(leftAt)} ({fmtTime(leftAt)})</span></>
                : <span style={{ color: '#6b7280', fontWeight: 400 }}>No runs yet {isToday ? 'today' : 'this day'}.</span>}
        </div>
        {isToday && vehicle && nowAt && (
          <div style={{ fontSize: 13, color: '#374151', marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
            <MapPin size={13} /> {gpsStale ? 'Last seen' : 'Now'}: {nowAt}
            {movingNow ? ` · ${Math.round(vehicle.speed_mph || 0)} mph`
              : gpsStale && vehicle.synced_at ? ` · ${fmtAgo(vehicle.synced_at)}` : ''}
          </div>
        )}
        {lastRun && !openStop && (
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
            {Number(lastRun.distance_miles).toFixed(1)} mi this run · {fmtDrive(lastRun.duration_min)}
          </div>
        )}
        <div style={{ marginTop: 6 }}>
          {onTrailer.length
            ? onTrailer.map(t => <span key={t.equipment_id} style={chip('#dbeafe', '#1e40af')}><Package size={13} /> {pillName(t.name)}</span>)
            : <span style={chip('#f3f4f6', '#6b7280')}>Empty trailer</span>}
        </div>
      </div>

      {/* Camera — defaults to the trailer (aux) cam so you can SEE the load */}
      {isToday && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 15, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Camera size={16} /> {featured ? (CAM_LABELS[featured.input] || featured.input) : 'Camera'}
              {featured && <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 400 }}>{fmtAgo(featured.startTime || featured.capturedAtTime)}</span>}
            </h3>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={loadCams} disabled={camsLoading} style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 8, padding: '5px 9px', cursor: 'pointer' }}>
                <RefreshCw size={14} style={camsLoading ? { animation: 'spin 1s linear infinite' } : undefined} />
              </button>
              <button onClick={openLiveShare} disabled={liveShareLoading} style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 8, padding: '5px 9px', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
                <ExternalLink size={13} /> Live Track
              </button>
            </div>
          </div>
          {camsLoading && !cams.length ? (
            <div style={{ textAlign: 'center', padding: 26 }}><Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} /></div>
          ) : featured ? (
            <>
              <img
                src={featured.url} alt={CAM_LABELS[featured.input] || featured.input}
                onClick={() => setCamModal(true)}
                style={{ width: '100%', borderRadius: 8, display: 'block', cursor: 'pointer' }}
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                {cams.map(c => (
                  <button key={c.input} onClick={() => pickCam(c.input)}
                    style={{
                      border: 'none', borderRadius: 999, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                      background: c.input === featured.input ? '#1e40af' : '#e5e7eb',
                      color: c.input === featured.input ? '#fff' : '#374151',
                    }}>
                    {CAM_LABELS[c.input] || c.input}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>No recent camera images.</p>
          )}
        </div>
      )}

      {/* Map — current run bold, whole-day trail muted; locked until double-tap */}
      <div style={{ ...card, padding: 0, overflow: 'hidden', position: 'relative' }}>
        <div ref={mapContainer} style={{ width: '100%', height: '62vh', minHeight: 340 }} />
        <div style={{
          position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(15,23,42,0.75)', color: '#fff', borderRadius: 999,
          padding: '6px 14px', fontSize: 12, fontWeight: 600, pointerEvents: 'none', whiteSpace: 'nowrap',
        }}>
          Double-tap: zoom to truck · again: back
        </div>
        {/* Guaranteed tap target for the same toggle */}
        <button
          onClick={() => zoomToggleRef.current?.()}
          style={{
            position: 'absolute', top: 10, left: 10, width: 40, height: 40, borderRadius: 10,
            border: '1px solid #d1d5db', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}
          aria-label="Zoom to truck"
        >
          🚛
        </button>
        {loading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.5)' }}>
            <Loader2 size={28} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        )}
      </div>

      {/* Timeline */}
      <div style={card}>
        <h3 style={{ margin: '0 0 8px', fontSize: 15, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Clock size={16} /> Day timeline
        </h3>
        {timeline.length === 0 ? (
          <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>
            {loading ? 'Loading…' : 'No GPS activity recorded for this day.'}
          </p>
        ) : timeline.map(item => {
          if (item.kind === 'stop') {
            const s = item.stop;
            return (
              <div key={`s${s.stop_id}`} style={{ borderLeft: '3px solid #2563eb', padding: '6px 0 6px 12px', marginBottom: 10 }}>
                <div style={{ fontWeight: 800, fontSize: 15 }}>
                  <span style={{ display: 'inline-flex', width: 20, height: 20, borderRadius: '50%', background: '#1e40af', color: '#fff', fontSize: 11, alignItems: 'center', justifyContent: 'center', marginRight: 6, verticalAlign: 'text-bottom' }}>{item.idx + 1}</span>
                  {s.name}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  {fmtTime(s.arrived_at)} – {s.departed_at ? fmtTime(s.departed_at) : 'still here'}
                  {s.dwell_minutes != null && ` · ${s.dwell_minutes} min`}
                </div>
                <div>
                  {/* Drops first — that's the physical order at a stop */}
                  {s.dropped.map(m => (
                    <span key={m.movement_id} style={chip('#fef3c7', '#92400e')}>
                      <ArrowDownToLine size={12} /> {pillName(m.name)}{m.status === 'provisional' ? ' ?' : ''}
                    </span>
                  ))}
                  {s.loaded.map(m => (
                    <span key={m.movement_id} style={chip('#dbeafe', '#1e40af')}>
                      <ArrowUpFromLine size={12} /> {pillName(m.name)}{m.status === 'provisional' ? ' ?' : ''}
                    </span>
                  ))}
                </div>
              </div>
            );
          }
          if (item.kind === 'gps') {
            const ge = item.ge;
            return (
              <div key={`g${ge.movement_id}`} style={{ borderLeft: '3px solid #2563eb', padding: '6px 0 6px 12px', marginBottom: 10 }}>
                <div style={{ fontWeight: 800, fontSize: 15 }}>{ge.job_name || 'Quick stop'}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{fmtTime(ge.detected_at)} · quick stop</div>
                <div>
                  <span style={chip(ge.action === 'load' ? '#dbeafe' : '#fef3c7', ge.action === 'load' ? '#1e40af' : '#92400e')}>
                    {ge.action === 'load' ? <ArrowUpFromLine size={12} /> : <ArrowDownToLine size={12} />} {pillName(ge.name)}{ge.status === 'provisional' ? ' ?' : ''}
                  </span>
                </div>
              </div>
            );
          }
          if (item.kind === 'driver') {
            const ev = item.ev;
            return (
              <div key={`d${ev.movement_id}`} style={{ borderLeft: '3px solid #9ca3af', padding: '4px 0 4px 12px', marginBottom: 8, fontSize: 13 }}>
                <span style={{ fontWeight: 700 }}>{ev.action === 'load' ? 'Picked up' : 'Dropped off'} {evLabel(ev)}</span>
                <span style={{ color: '#6b7280' }}> · {fmtTime(ev.timestamp)} · driver{ev.job_name ? ` · ${ev.job_name}` : ''}</span>
              </div>
            );
          }
          const dl = item.dl;
          return (
            <div key={`l${dl.load_id}`} style={{ borderLeft: '3px solid #65a30d', padding: '4px 0 4px 12px', marginBottom: 8, fontSize: 13 }}>
              <span style={{ fontWeight: 700 }}>{dl.load_count}× {dl.material_type}</span>
              <span style={{ color: '#6b7280' }}> · {fmtTime(dl.timestamp)}{dl.job_name ? ` · ${dl.job_name}` : ''}</span>
            </div>
          );
        })}
      </div>

      {/* All-cameras modal */}
      {camModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, overflowY: 'auto', padding: 16 }} onClick={() => setCamModal(false)}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={() => setCamModal(false)} style={{ border: 'none', background: 'none', color: '#fff', padding: 8, cursor: 'pointer' }}><X size={26} /></button>
          </div>
          {cams.map(c => (
            <div key={c.input} style={{ marginBottom: 16 }} onClick={e => e.stopPropagation()}>
              <div style={{ color: '#fff', fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
                {CAM_LABELS[c.input] || c.input} <span style={{ fontWeight: 400, opacity: 0.7 }}>{fmtAgo(c.startTime || c.capturedAtTime)}</span>
              </div>
              <img src={c.url} alt={c.input} style={{ width: '100%', borderRadius: 8 }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
